import { Hash } from 'lucide-react';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useGlobalSchemaStore } from '../global-schema-store';
import { walkColumns, connectionLabel } from './_schema-helpers';
import type { SearchItem, SearchProvider } from '../types';
import type { ConnectionConfig } from '@/types';

/**
 * Column search across every cached schema. On a large warehouse this can
 * easily exceed 20k columns, so the provider caps itself at
 * `search.columnResultCap` (default 200, configurable in Settings → UI).
 *
 * The cap is applied before scoring on purpose — the scorer can handle 200
 * items instantly, but building 20k SearchItem objects per open would make
 * the bar feel sluggish. Users who need global coverage can lift the cap.
 */
export const columnSearchProvider: SearchProvider = {
  id: 'columns',
  kind: 'column',
  list: (ctx) => {
    const schemas = useGlobalSchemaStore.getState().schemas;
    const connections = ctx.queryClient?.getQueryData<ConnectionConfig[]>(['connections']) ?? [];
    const cap = parseInt(
      useSettingsStore.getState().get('search.columnResultCap', '200'),
      10,
    ) || 200;

    const items: SearchItem[] = [];
    for (const loc of walkColumns(schemas)) {
      const connName = connectionLabel(connections, loc.connectionId);
      items.push({
        id: `column:${loc.connectionId}:${loc.database}:${loc.schema}.${loc.table.name}.${loc.columnName}`,
        kind: 'column',
        label: loc.columnName,
        subtitle: `${loc.columnType} · ${loc.schema}.${loc.table.name} · ${loc.database} · ${connName}`,
        icon: Hash,
        boost: 0.8,
        action: () => {
          // No scroll-to-column yet — just open the table; the sidebar
          // highlight already nudges the user to the match.
          const tabId = `table:${loc.connectionId}:${loc.schema}.${loc.table.name}`;
          useWorkspaceStore.getState().openTab({
            type: 'table',
            id: tabId,
            connectionId: loc.connectionId,
            database: loc.database,
            schema: loc.schema,
            table: loc.table.name,
            where: '',
            orderBy: '',
          });
        },
      });
      if (items.length >= cap) break;
    }

    return items;
  },
};
